import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Vérifier si l'utilisateur est connecté au chargement
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data.data.user);
    } catch (error) {
      // Token invalide, déconnecter
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, recaptchaToken = null) => {
    try {
      const response = await api.post('/auth/login', { 
        email, 
        password,
        recaptchaToken 
      });
      const { token: newToken, user: userData, forcePasswordChange } = response.data.data;
      
      setToken(newToken);
      setUser(userData);
      localStorage.setItem('auth_token', newToken);
      
      return { success: true, user: userData, forcePasswordChange };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur de connexion',
      };
    }
  };

  const register = async (name, email, password, password_confirmation, recaptchaToken = null) => {
    try {
      const response = await api.post('/auth/register', {
        name,
        email,
        password,
        password_confirmation,
        recaptchaToken,
      });
      const { token: newToken, user: userData } = response.data.data;
      
      setToken(newToken);
      setUser(userData);
      localStorage.setItem('auth_token', newToken);
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur d\'inscription',
        errors: error.response?.data?.errors,
      };
    }
  };

  const loginGoogle = async (credential) => {
    try {
      const response = await api.post('/auth/google', { credential });
      const { token: newToken, user: userData } = response.data.data;
      
      setToken(newToken);
      setUser(userData);
      localStorage.setItem('auth_token', newToken);
      
      return { success: true, user: userData };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur de connexion Google',
      };
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Ignorer les erreurs de déconnexion
    } finally {
      setToken(null);
      setUser(null);
      localStorage.removeItem('auth_token');
    }
  };

  const isAdmin = user && (user.isAdmin || user.role === 'admin');
  const isAssistant = user && user.role === 'assistant' && !isAdmin;
  const isStaff = isAdmin || isAssistant;

  const value = {
    user,
    token,
    loading,
    login,
    register,
    loginGoogle,
    logout,
    isAdmin,
    isAssistant,
    isStaff,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

